import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Calendar, MessageCircle, User, LogOut, Settings, Users, Mail, Sun, Moon, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin, isTrainer } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/user/dashboard' },
    { icon: Calendar, label: 'My Bookings', path: '/user/bookings' },
    { icon: MessageCircle, label: 'Messages', path: '/user/messages' },
    { icon: User, label: 'Profile', path: '/user/profile' },
  ];

  const trainerNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/trainer/dashboard' },
    { icon: MessageCircle, label: 'Messages', path: '/trainer/messages' },
  ];

  const adminNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Trainers', path: '/admin/trainers' },
    { icon: User, label: 'Users', path: '/admin/users' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
    { icon: Shield, label: 'Account', path: '/admin/account' },
  ];

  const navItems = isAdmin ? adminNavItems : isTrainer ? trainerNavItems : userNavItems;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Top Header */}
      <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b-2 border-red-200 dark:border-gray-700 sticky top-0 z-50 shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center transform hover:scale-105 transition-transform">
              <img 
                src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'} 
                alt="TrainerList" 
                className="h-10 sm:h-12 w-auto object-contain"
              />
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="text-right bg-gradient-to-br from-red-100 to-gray-100 dark:from-gray-700 dark:to-gray-800 px-4 py-2 rounded-xl">
                <p className="font-bold text-sm text-red-700 dark:text-red-400">{user?.name}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 capitalize font-semibold">{user?.role}</p>
              </div>
              {/* Theme Toggle Button */}
              <Button
                onClick={toggleTheme}
                variant="outline"
                size="sm"
                className="border-2 border-red-300 dark:border-gray-600 hover:bg-gradient-to-r hover:from-red-600 hover:to-black hover:text-white hover:border-transparent transition-all dark:text-gray-300 p-2"
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm" className="border-2 border-red-300 dark:border-gray-600 hover:bg-gradient-to-r hover:from-red-600 hover:to-black hover:text-white hover:border-transparent transition-all dark:text-gray-300">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Hidden on mobile, visible on larger screens */}
        <aside className="hidden lg:block w-64 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-r-2 border-red-200 dark:border-gray-700 min-h-[calc(100vh-73px)] p-4 shadow-lg">
          <nav className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium group transform hover:scale-105 ${
                    isActive 
                      ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg' 
                      : 'hover:bg-gradient-to-r hover:from-red-100 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:to-gray-600 text-red-700 dark:text-gray-300 hover:text-red-900 dark:hover:text-white'
                  }`}
                >
                  <Icon className={`w-5 h-5 group-hover:scale-110 transition-transform ${isActive ? 'text-white' : ''}`} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-2 sm:p-4 lg:p-8 overflow-x-hidden">
          <div className="max-w-7xl mx-auto w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;